import { z } from 'zod';

export const boundaryPointSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});

export type BoundaryPoint = z.infer<typeof boundaryPointSchema>;

const closedPolygon = z
  .array(boundaryPointSchema)
  .min(3, { message: 'Polygon must have at least 3 points' })
  .superRefine((points, ctx) => {
    const first = points[0];
    const last = points[points.length - 1];
    if (!first || !last) return;
    if (first.lat !== last.lat || first.lng !== last.lng) {
      ctx.addIssue({
        code: 'custom',
        message:
          'Polygon must be closed: first and last points must be identical',
        path: [points.length - 1],
      });
    }
  });

export const customRegionItemSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  description: z.string().nullable(),
  countries: z.array(z.string()),
  cities: z.array(z.string()).nullable(),
  labels: z.array(z.string()).nullable(),
  boundaryPoints: z.array(boundaryPointSchema),
  parentId: z.uuid().nullable(),
  organizationId: z.uuid(),
  createdBy: z.uuid(),
  createdOn: z.string(),
  modifiedOn: z.string().nullable(),
});

export type CustomRegionItem = z.infer<typeof customRegionItemSchema>;

export const createCustomRegionInput = z.object({
  title: z.string().min(1),
  boundaryPoints: closedPolygon,
  countries: z.array(z.string().min(1)).min(1),
  cities: z.array(z.string()).optional(),
  labels: z.array(z.string()).optional(),
  description: z.string().optional(),
  parentId: z.uuid().optional(),
  expandParent: z.boolean().optional(),
});

export type CreateCustomRegionInput = z.infer<typeof createCustomRegionInput>;

export const updateCustomRegionInput = z
  .object({
    id: z.uuid(),
    title: z.string().min(1).optional(),
    boundaryPoints: closedPolygon.optional(),
    countries: z.array(z.string().min(1)).min(1).optional(),
    cities: z.array(z.string()).nullable().optional(),
    labels: z.array(z.string()).nullable().optional(),
    description: z.string().nullable().optional(),
    parentId: z.uuid().nullable().optional(),
    expandParent: z.boolean().optional(),
  })
  .refine(
    (p) =>
      Object.entries(p).some(
        ([key, value]) => key !== 'id' && value !== undefined
      ),
    { message: 'patch must contain at least one field' }
  );

export type UpdateCustomRegionInput = z.infer<typeof updateCustomRegionInput>;

export const findCustomRegionsInput = z.object({
  countries: z.array(z.string()).optional(),
  cities: z.array(z.string()).optional(),
  labels: z.array(z.string()).optional(),
  title: z.string().optional(),
  offset: z.number().int().min(0).optional(),
  limit: z.number().int().min(1).max(200).optional(),
});

export type FindCustomRegionsInput = z.infer<typeof findCustomRegionsInput>;

export const findCustomRegionsResultSchema = z.object({
  data: z.array(customRegionItemSchema),
  count: z.number().int().nonnegative(),
  countAll: z.number().int().nonnegative(),
});

export type FindCustomRegionsResult = z.infer<
  typeof findCustomRegionsResultSchema
>;

export const deleteCustomRegionInput = z.object({ id: z.uuid() });
export type DeleteCustomRegionInput = z.infer<typeof deleteCustomRegionInput>;

export const deleteCustomRegionResultSchema = z.object({ id: z.uuid() });
export type DeleteCustomRegionResult = z.infer<
  typeof deleteCustomRegionResultSchema
>;
